import type { FilterSchema, RemoteFilterOptions } from "@playbookai/ui";
import type {
  KpiDefinition,
  KpiDirection,
  ManagedKpiSummary,
} from "../api/types.js";
import { DIRECTION_LABEL } from "../components/kpi-presentation.js";

/**
 * KPI filter schemas (PB-2941) — the ONE feature-owned place declaring which
 * filters each KPI Manager page has, where their choices come from, and how a
 * selection applies. The shared `@playbookai/ui` FilterMenu renders and runs
 * these; the components only mount a controller + menu + chip rail.
 *
 * (Named `kpi-filter-schema`, not `kpi-filters` — `components/kpi-filters.ts`
 * is the unrelated legacy toolbar→query helper.)
 */

export const KPI_UNIT_FILTER_ID = "unit";
export const KPI_DIRECTION_FILTER_ID = "direction";
export const KPI_OWNER_FILTER_ID = "owner";

/** The filter menu speaks the SAME direction labels the rest of the KPI surface
 * does. Re-exported rather than re-declared: PB-2941 added a third direction,
 * and a second hand-written copy is how one of them ends up saying
 * `TARGET_RANGE` in a chip. */
export const KPI_DIRECTION_LABELS: Record<KpiDirection, string> =
  DIRECTION_LABEL;

/**
 * KPI Library filters: Unit + Direction, both LOCAL — the Library already
 * holds the complete definitions response, so choices derive from it and
 * application is in-memory. No option request is ever made. Collection and
 * Category stay browsing rails on the page, not filter fields.
 */
export function buildKpiLibraryFilterSchema(
  definitions: readonly KpiDefinition[],
): FilterSchema<KpiDefinition> {
  const units = [
    ...new Set(definitions.map((definition) => definition.unit)),
  ].sort((a, b) => a.localeCompare(b));
  return [
    {
      id: KPI_UNIT_FILTER_ID,
      label: "Unit",
      type: "single",
      anyLabel: "All units",
      options: {
        kind: "static",
        options: units.map((value) => ({ value, label: value })),
      },
      apply: {
        local: (definition, values) => values.includes(definition.unit),
      },
    },
    {
      id: KPI_DIRECTION_FILTER_ID,
      label: "Direction",
      type: "single",
      anyLabel: "All directions",
      options: {
        kind: "static",
        options: (
          Object.entries(KPI_DIRECTION_LABELS) as Array<[KpiDirection, string]>
        ).map(([value, label]) => ({ value, label })),
      },
      apply: {
        local: (definition, values) => values.includes(definition.direction),
      },
    },
  ];
}

/**
 * All KPIs filters: Owner — remote (the list is paginated, so options must
 * cover every distinct owner, not a loaded page) and REQUEST-MAPPED onto the
 * existing `GET /v1/kpis` read via `ownerUserIds`.
 */
export function buildAllKpisFilterSchema(
  ownerOptions: RemoteFilterOptions,
): FilterSchema<ManagedKpiSummary> {
  return [
    {
      id: KPI_OWNER_FILTER_ID,
      label: "Owner",
      type: "multi",
      options: ownerOptions,
      apply: { requestParam: "ownerUserIds" },
    },
  ];
}
